import React from 'react';
import { flexRender } from '@tanstack/react-table';
import type { Column, Table as TanstackTable } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

// @tanstack/react-table is headless, so we render our own table markup. These
// styled native elements carry over the look of the former react-table-6
// `styled(ReactTableDefaults.*)` slots.

// The table is the one and only scroll container: header and body live inside
// it and therefore scroll together, horizontally, for free. The previous design
// gave Thead and Tbody a scroller each and kept them in step with JS
// (syncHeaderScroll). That needs the two panes to agree on their usable width
// to the pixel, and anything that widens one but not the other — Tr's 5px
// selection gutter, a scrollbar gutter — desynchronises the columns at the
// right end of the range. Scrolling one box cannot drift from itself.
const StyledTable = styled.table`
  background: none;
  font-size: ${({ theme }) => theme.fontSizes.p};
  color: ${({ theme }) => theme.colors.text};
  display: block;
  flex: 1;
  border: 0;
  border-collapse: collapse;
  min-height: 0;
  width: 100%;
  overflow: auto;
  /* Covers the area below the last row and to the right of the columns, which
     no Tr background reaches. */
  background-color: ${({ theme }) => theme.colors.white};

  /* Overlay scrollbars (the macOS default) stay invisible until the user
     already knows to scroll, so a table whose columns run past the right edge
     looks merely truncated. Styling the scrollbar opts this pane out of overlay
     behaviour and keeps it on screen whenever there is overflow. Matches the
     .fjcloud-scrollbar convention in global-styles.
     Deliberately no scrollbar-width/scrollbar-color alongside these: setting
     either makes Chrome switch to the standard scrollbar and ignore the rules
     below, silently restoring overlay scrollbars. Firefox draws a non-overlay
     scrollbar of its own regardless. */
  &::-webkit-scrollbar {
    height: 10px;
    width: 10px;
    background-color: ${({ theme }) => theme.colors.defaultLightBackgroundColor};
  }
  &::-webkit-scrollbar-thumb {
    background-color: orange;
    border-radius: 4px;
  }
`;

// Sticky rather than a scroller of its own: it rides the table's horizontal
// scroll automatically and only needs pinning on the vertical axis.
// `min-width: min-content` so the black strip spans the full scroll width
// rather than stopping at the visible container's edge.
const Thead = styled.thead`
  display: block;
  position: sticky;
  top: 0;
  z-index: 1;
  box-shadow: 10px 0px 14px 0 rgba(0, 0, 0, 0.4);
  min-width: min-content;
  min-height: ${({ theme }) => theme.grid.units(2)};
  height: auto;
  background-color: ${({ theme }) => theme.colors.black};
`;

/* `min-width: min-content` makes the row box as wide as the columns it holds
   (the sum of their widths/min-widths) rather than only as wide as the visible
   container, so the header bar stays black across the whole scroll range
   instead of running out into white past the container's right edge. */
const HeaderRow = styled.tr`
  display: flex;
  min-width: min-content;
  min-height: ${({ theme }) => theme.grid.units(2)};
  height: auto;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  border: 0;
`;

const Th = styled.th<{ $sortable: boolean }>`
  position: relative;
  border: 0;
  min-height: ${({ theme }) => theme.grid.units(2)};
  display: flex;
  padding: 0 5px;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  align-items: stretch;

  > div {
    flex: 1;
    display: flex;
    font-size: inherit;
    align-items: center;
  }
`;

const Resizer = styled.div<{ $isResizing: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 6px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  background: ${({ $isResizing, theme }) =>
    $isResizing ? theme.colors.selectableLightBg : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.selectableLightBg};
  }
`;

// Plain block: the table above is the scroll container now.
const Tbody = styled.tbody`
  display: block;
  min-width: min-content;
`;

// See HeaderRow for why `min-width: min-content`: it keeps the row stripes
// spanning the full scroll width rather than stopping at the container edge.
const Tr = styled.tr<{ $smallRows?: boolean; $odd?: boolean; $selected?: boolean }>`
  display: flex;
  position: relative;
  min-width: min-content;
  height: ${({ theme, $smallRows }) => theme.grid.units($smallRows ? 2 : 3)};
  background-color: ${({ theme, $odd }) => ($odd ? theme.colors.rowStripe : theme.colors.white)};
  border-left-style: solid;
  border-left-color: ${({ theme }) => theme.colors.selected};
  margin-left: ${({ $selected }) => ($selected ? 0 : 5)}px;
  border-left-width: ${({ $selected }) => ($selected ? 5 : 0)}px;
`;

const Td = styled.td`
  display: flex;
  border: 0;
  align-items: center;
`;

// Column sizing. Columns with an explicit `size` (or that the user has
// actually dragged via `columnSizing`) get a fixed width; columns with
// neither flex to fill the available space, same as before resizing existed.
const cellSizeStyle = <TData,>(
  column: Column<TData, unknown>,
  columnSizing: Record<string, number>,
): CSSProperties => {
  const { minSize, maxSize } = column.columnDef;
  const size = columnSizing[column.id] ?? column.columnDef.size;
  return {
    flex: size != null ? '0 0 auto' : '1 1 0',
    ...(size != null && { width: `${size}px` }),
    ...(minSize != null && { minWidth: `${minSize}px` }),
    ...(maxSize != null && { maxWidth: `${maxSize}px` }),
  };
};

interface TableProps<TData> {
  table: TanstackTable<TData>;
  selectedRowsByID?: Record<string, boolean> | null;
  smallRows?: boolean;
}

export const Table = <TData,>({
  table,
  selectedRowsByID,
  smallRows = false,
}: TableProps<TData>): React.ReactElement => {
  const { columnSizing } = table.getState();
  return (
    <StyledTable>
      <Thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <HeaderRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortable = header.column.getCanSort();
              const resizable = header.column.getCanResize();
              return (
                <Th
                  key={header.id}
                  $sortable={sortable}
                  style={cellSizeStyle(header.column, columnSizing)}
                  onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                >
                  <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                  {resizable && (
                    <Resizer
                      data-testid={`${header.id}-resizer`}
                      $isResizing={header.column.getIsResizing()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    />
                  )}
                </Th>
              );
            })}
          </HeaderRow>
        ))}
      </Thead>
      <Tbody>
        {table.getRowModel().rows.map((row, index) => {
          const rowId = (row.original as { id?: string | number }).id;
          const selected = !!(selectedRowsByID && rowId != null && selectedRowsByID[rowId]);
          return (
            <Tr key={row.id} $selected={selected} $odd={index % 2 === 1} $smallRows={smallRows}>
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id} style={cellSizeStyle(cell.column, columnSizing)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          );
        })}
      </Tbody>
    </StyledTable>
  );
};

export const ControlsWrapper = styled.div`
  display: flex;
  padding-right: ${({ theme }) => theme.grid.units(1)};
  align-items: center;
  height: ${({ theme }) => theme.grid.units(3)};
  color: ${({ theme }) => theme.colors.selectableLightBg};
  background-color: white;
`;
